import './styles/Login.css';

import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import {
  type AvailableIdps,
  type DexProvider,
  MAX_USERNAME_LENGTH,
  MAX_USER_PASSWORD_LENGTH,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { login } from 'components/GlobalState/commands/login';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { type FormikErrors, useFormik } from 'formik';
import { type FC, memo, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import type { AppTranslations } from 'translations/translations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { useApi } from 'utils/request/useApi';
import { SingleSignOn } from './SingleSignOn';

interface LoginFormValues {
  username: string;
  password: string;
}

const makeValidate =
  (msg: AppTranslations['auth']['validation']) =>
  (values: LoginFormValues): FormikErrors<LoginFormValues> => {
    const errors: FormikErrors<LoginFormValues> = {};
    if (!values.username) {
      errors.username = msg.required;
    } else if (values.username.length > MAX_USERNAME_LENGTH) {
      errors.username = msg.usernameTooLong;
    }
    if (!values.password) {
      errors.password = msg.required;
    } else if (values.password.length > MAX_USER_PASSWORD_LENGTH) {
      errors.password = msg.passwordTooLong;
    }
    return errors;
  };

const stripMinorVersion = (endpoint: string) => {
  const minorVersionRegex = /^(https?:\/\/[^\/]+(?:\/api)?\/v\d+)\.\d+(\/.*)$/;

  const minorVersionExists = endpoint.match(minorVersionRegex);
  if (minorVersionExists?.[1] && minorVersionExists?.[2]) {
    return `${minorVersionExists[1]}${minorVersionExists[2]}`;
  }
  return endpoint;
};

const BareLogin: FC = () => {
  const ctxProps = useGlobalStateContextSelectors({
    dispatch: prop('dispatch'),
    loginStatus: prop('loginStatus'),
  });
  const [loadingIdps, setLoadingIdps] = useLoadingState();
  const [redirectingToSso, setRedirectingToSso] = useLoadingState();
  const [idps, setIdps] = useState<DexProvider[]>([]);
  const [authFormEnabled, setAuthFormEnabled] = useState<boolean | null>(null);
  const api = useApi();
  const navigate = useNavigate();

  const handleSubmit = useStableCallback(async () => {
    await ctxProps.dispatch(login, formik.values);
  });

  const {
    auth: {
      form: msg,
      validation: validationMsg,
      singleSignOn: singleSignOnMsg,
    },
  } = useLocale();
  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validate: makeValidate(validationMsg),
    onSubmit: makeFormikOnSubmit(handleSubmit),
  });

  const fetchAvailableIdps = async () => {
    const response = await withLoadingIndicator(setLoadingIdps)(() =>
      api.request<AvailableIdps>({
        url: '/auth/available-idps',
        method: 'GET',
      }),
    );

    const { identityProviders, isLdapEnabled, isLocalAuthenticationEnabled } =
      response;

    const strippedIdentityProviders = identityProviders?.map(
      (provider: DexProvider) => ({
        name: provider.name,
        url: stripMinorVersion(provider.url),
      }),
    );

    setIdps(strippedIdentityProviders);
    setAuthFormEnabled(isLdapEnabled || isLocalAuthenticationEnabled);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: perform only on mount
  useEffect(() => {
    if (ctxProps.loginStatus === 'AUTHENTICATED') {
      navigate('/');
    } else {
      fetchAvailableIdps();
    }
  }, []);

  if (loadingIdps !== 'LOADED') {
    return <LoaderAndText />;
  }

  if (redirectingToSso === 'LOADING') {
    return <LoaderAndText bottomText='Redirecting to the Identity Provider' />;
  }

  return (
    <div className='login'>
      <div className='login__box'>
        <Helmet title='Login' />
        <h1 className='login__header'>{msg.legend}</h1>
        {authFormEnabled && (
          <>
            <form className='login__form' onSubmit={formik.handleSubmit}>
              <fieldset disabled={formik.isSubmitting}>
                <div className='form-group'>
                  <FormikTextField
                    autoFocus
                    label={msg.label.username}
                    name='username'
                    formik={formik}
                    type='text'
                  />
                </div>
                <div className='form-group'>
                  <FormikTextField
                    label={msg.label.password}
                    name='password'
                    formik={formik}
                    type='password'
                  />
                </div>
              </fieldset>
              <div className='flex justify-center'>
                <Button
                  type='submit'
                  loading={formik.isSubmitting}
                  variant='primary'
                  accent='positive'
                  size='sm'
                >
                  {msg.button.login}
                </Button>
              </div>
            </form>
            <p className='mb-0 pb-4 pt-6 text-center text-sm'>
              {singleSignOnMsg.loginWithIP}
            </p>
          </>
        )}
        <SingleSignOn
          identityProviders={idps}
          onRedirectToSso={() => {
            setRedirectingToSso('LOADING');
          }}
        />
      </div>
      <div className='login__security'>
        <p className='font-semibold'>{msg.security.title}</p>
        <p>{msg.security.content}</p>
      </div>
    </div>
  );
};

export const Login = memo(BareLogin);
